import React, { FC, useState, useEffect, useRef } from 'react';
import { ConnectedProps, connect } from 'react-redux';
import {
    MessageBar, Language, FilteredColumn, TextField, SectionSize,
    Section, Panel, Form, requiredItem, ApiException
} from '@eta/components';
import { MessageBarType, DefaultButton, PrimaryButton, ActionButton, CommandBar, ICommandBarItemProps, IDropdownOption, SpinnerSize, Spinner } from '@fluentui/react';
import { useTranslation } from 'react-i18next';
import { isNullOrUndefined } from 'util';
import { useHistory } from 'react-router-dom';
import { ServiceCreator } from '../Service/client';
import './IntermediaryDetails.scss';
import { RepresentedTaxpayersResponseDto, AddressDto, IntermediaryStatuses, MetaData, IntermediaryDetailsResponseDto, UpdateContactDetailsDto } from '../Service/proxy';
import { LinkedTaxpayerGrid } from './LinkedTaxpayerGrid';
import { UIHelpers } from '../../helpers/UIHelpers';
import { ApplicationHistory } from './ApplicationHistory';
import { StatusHistory } from './StatusHistory';
import { ReduxState } from '../../common/reducers';
import { Defaults } from '../../common/config/constants';
import { Email, Mobile, EmailMobile } from '../../Verification/Components';
import IntermediaryAddress from './IntermediaryAddress';
import { ChangeAppSatusAction, getCurrentIntermediary } from '../Redux/Actions';

const emptyIntermediary = new IntermediaryDetailsResponseDto();
const mapStateToProps = (state: ReduxState) => ({
    currentLanguage: state.reduxlanguage.language,
    referenceData: state.referenceData?.taxpayersConfig
        ? {
            optionsCity:
                state.referenceData?.taxpayersConfig?.cities?.map<
                    IDropdownOption
                >((v) => {
                    return {
                        key: v.id || 0,
                        text: v.name || '',
                        data: v.parentCode || ''
                    };
                }) ?? [],
            optionsGovernorate:
                state.referenceData?.taxpayersConfig?.governorates?.map<
                    IDropdownOption
                >((v) => {
                    return {
                        key: v.id || 0,
                        text: `${v.name}` || '',
                        data: v.code || ''
                    };
                }) ?? [],
            optionsCountry:
                state.referenceData?.taxpayersConfig?.countries?.map<
                    IDropdownOption
                >((v) => {
                    return {
                        key: v.id || 0,
                        text: `${v.name}` || '',
                        data: `${v.code}`
                    };
                }) ?? [],
        }
        : null,
    intermediary: state.Intermediary.intermediaryDetails ?? emptyIntermediary
});

const mapDispatchToProps = {
    dispatchChangeAppSatusAction: ChangeAppSatusAction,
    dispatchLoadIntermediary: getCurrentIntermediary,
};

interface Props {
    isTaxpayerAllowed: boolean;
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
const IntermediaryDetails: FC<PropsFromRedux & Props> = (props: PropsFromRedux & Props) => {
    const { t } = useTranslation(['intermediaries', 'common']);
    const { currentLanguage: propsCurrentLanguage, dispatchChangeAppSatusAction, isTaxpayerAllowed,
        referenceData, intermediary, dispatchLoadIntermediary } = props;
    const [currentLanguage, setcurrentLanguage] = useState<Language>(propsCurrentLanguage);
    const [showMessageSuccess, setShowMessageSuccess] = useState<boolean>(false);
    const [error, setError] = useState<string>();
    const [, setIsLoading] = useState<boolean>(true);
    const [isEditable, setEditable] = useState<boolean>(false);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [metadata, setMetadata] = useState<MetaData | undefined>();
    const [apiErrors, setApiErrors] = useState<ApiException>();

    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isApiCallLoading, setIsApiCallLoading] = useState<boolean>(false);

    const [showApplicationHistory, setShowApplicationHistory] = useState<boolean>(false);
    const [showAccessHistory, setShowAccessHistory] = useState<boolean>(false);

    const [showValidationEmail, setShowValidationEmail] = useState<boolean>(false);
    const [showValidationMobile, setShowValidationMobile] = useState<boolean>(false);
    const [showValidationEmailMobile, setShowValidationEmailMobile] = useState<boolean>(false);

    const [filters, setFilters] = useState<FilteredColumn[]>([]);
    const [pageSize, setPageSize] = useState<number>(Defaults.PageSize);

    const [currentIntermediary, setCurrentIntermediary] = useState<IntermediaryDetailsResponseDto | undefined>(undefined);
    const [linkedTaxpayers, setLinkedTaxpayers] = useState<RepresentedTaxpayersResponseDto[] | undefined>(undefined);
    const [isBlocked, setIsBlocked] = useState<boolean>(false);

    const history = useHistory();
    const _items: ICommandBarItemProps[] = [
        {
            key: 'RenewIntermediary',
            text: t('RenewIntermediary'),
            iconProps: { iconName: 'Redo' },
            disabled: isBlocked || isEditable,
            style: isBlocked ? { display: 'none' } : {},
            onClick: () => {
                dispatchChangeAppSatusAction(true);
                history.push(
                    '/TaxpayerProfile/RegisterIntermediary'

                );
            }
        },
        {
            key: 'Edit',
            text: t('common:edit'),
            iconProps: { iconName: 'Edit' },
            onClick: () => setEditable(true)
        }
    ];

    const form = useRef(new Form({}));
    const [isFormValid, setIsFormValid] = useState<boolean>(
        form.current.isValid
    );

    form.current.onValidityChanged = (isValid) => setIsFormValid(isValid);

    const onChangeEdit = (value: boolean) => {
        setEditable(value);
    };

    const loadRepresentedTaxpayers = async (pageNumber?: number, ps?: number, taxpayerName?: string, taxpayerNameAr?: string, id?: number, dateFrom?: Date, dateTo?: Date) => {
        try {
            const client = ServiceCreator.getProviderManagementPublicClient();
            const response = await client.getRepresentedTaxpayers(taxpayerName, taxpayerNameAr, id, dateFrom, dateTo, ps, pageNumber);
            setLinkedTaxpayers(response.result);
            setMetadata(response.metadata);
        } catch (ex) {
            const exception = ex as ApiException;
            setError(exception.serviceError?.toString());
        }
        setIsApiCallLoading(false);
    };

    useEffect(() => {
        if (intermediary === emptyIntermediary) {
            setIsLoading(true);
            dispatchLoadIntermediary();
        } else {
            setCurrentIntermediary(intermediary);
        }
    }, [intermediary, dispatchLoadIntermediary]);

    const loadData = async () => {
        try {
            setCurrentIntermediary(intermediary);
            setIsBlocked(intermediary.status === IntermediaryStatuses.Blocked);
            // loadRepresentedTaxpayers(currentPage, pageSize);
        } catch (ex) {
            const exception = ex as ApiException;
            setError(exception.serviceError?.toString());
        }
    };

    const cancelForm = () => {
        setEditable(false);
        setError('');
        setApiErrors(undefined);
        loadData();
    };

    const updateContactDetails = async () => {
        let saved = false;
        try {
            const client = ServiceCreator.getProviderManagementPublicClient();
            const request = new UpdateContactDetailsDto();
            request.address = currentIntermediary?.address;
            request.url = currentIntermediary?.url;
            request.email = currentIntermediary?.email;
            request.mobileNo = currentIntermediary?.mobileNo;
            await client.updateContactDetails(request);
            saved = true;
        } catch (ex) {
            const exception = ex as ApiException;
            setApiErrors(exception);
            setError(exception.serviceError?.toString());
        }
        return saved;
    };

    const saveForm = async () => {
        setIsSaving(true);
        if (form.current.isValid) {
            const saved = await updateContactDetails();
            if (saved) {
                setEditable(false);
                setError('');
                setShowMessageSuccess(true);
                dispatchLoadIntermediary();
            }
        }
        setIsSaving(false);
    };

    useEffect(() => {
        if (currentIntermediary === emptyIntermediary) {
            setIsLoading(true);
            dispatchLoadIntermediary();
        } else {
            setCurrentIntermediary(currentIntermediary);
        }
    }, [currentIntermediary, dispatchLoadIntermediary]);

    const formattedAddress = (address?: AddressDto): string => {
        const cityNameEG = referenceData?.optionsCity?.filter((i) => i.key === address?.cityID)[0]?.text;
        const governNameEG = referenceData?.optionsGovernorate?.filter((i) => i.key === address?.governorateID)[0]?.text;
        const countryName = referenceData?.optionsCountry?.filter((i) => i.key === address?.countryID)[0]?.text;
        const cityName = address?.countryCode === 'EG' || address?.countryID === 1 ? cityNameEG : address?.foreignCityName;
        const governName = address?.countryCode === 'EG' || address?.countryID === 1 ? governNameEG : address?.foreignGovernorateName;

        const formattedAddr =
            `${address?.flatNo || ''}, ${address?.floorNo || ''}, ${address?.buildingNo || ''} ${address?.streetName || ''}, ${address?.streetName2 || ''}, ${cityName || ''}, ${governName || ''}, ${countryName || ''}, ${address?.postalCode || ''}`;

        return formattedAddr;
    };

    const onPageChanged = (pageNum: number) => {
        setIsApiCallLoading(true);
        setCurrentPage(pageNum + 1);
        return true;
    };

    useEffect(() => {
        if (isNullOrUndefined(currentIntermediary)) {
            loadData();
        }
    }, [intermediary]);

    useEffect(() => {
        if (currentLanguage.lang !== propsCurrentLanguage.lang) {
            setcurrentLanguage(propsCurrentLanguage);
        }
    }, [propsCurrentLanguage, currentLanguage]);

    const filterTaxpayers = (pageNo: number, nbItemPerPage: number, appliedFilters: FilteredColumn[]) => {
        setIsLoading(true);
        const name = appliedFilters && appliedFilters.find((i: FilteredColumn) => i.criteria[0] && i.criteria[0].fieldName === 'namePrimaryLang');
        const nameArabic = appliedFilters && appliedFilters.find((i: FilteredColumn) => i.criteria[0] && i.criteria[0].fieldName === 'nameSecondaryLang');

        const nameEn = name && name.criteria?.[0]?.filterValue?.toString();
        const nameAr = nameArabic && nameArabic.criteria?.[0]?.filterValue?.toString();
        loadRepresentedTaxpayers(pageNo, nbItemPerPage, nameEn, nameAr);

        setIsLoading(false);
    };

    const onNbItemPerPageChanged = (nbItemPerPage: number) => {
        setIsApiCallLoading(true);
        setPageSize(nbItemPerPage);
        filterTaxpayers(1, nbItemPerPage, filters);
    };

    const onGridChanged = (pageIndex: number, filteredColumns: FilteredColumn[]) => {
        setIsApiCallLoading(true);
        setFilters(filteredColumns);
        filterTaxpayers(pageIndex + 1, pageSize, filteredColumns);
        return false;
    };

    const onSave = () => {
        setShowValidationEmail(false);
        setShowValidationMobile(false);
        setShowValidationEmailMobile(false);
    };

    return (
        <div className='intermediaries'>
            {showMessageSuccess && (
                <MessageBar
                    messageBarType={MessageBarType.success}
                    isMultiline={false}
                    className="messageBar"
                >
                    {t('common:SavedSuccessfully')}
                </MessageBar>
            )}
            {error && (
                <MessageBar
                    messageBarType={MessageBarType.error}
                    isMultiline={false}
                    className="messageBar"
                >
                    {error}
                </MessageBar>
            )}
            <div>
                <Panel
                    title={t('ContactDetails')}
                    allowEdition={isEditable}
                    editable={isEditable}
                    onChangeEdit={onChangeEdit}
                    footer={(
                        <>
                            <DefaultButton
                                text={t('common:cancel')}
                                onClick={() => cancelForm()}
                            />
                            <PrimaryButton
                                text={t('common:save')}
                                onClick={() => saveForm()}
                                disabled={isSaving || !isFormValid}
                            >
                                {isSaving && <Spinner size={SpinnerSize.small} />}
                            </PrimaryButton>
                        </>
                    )}
                >
                    {isTaxpayerAllowed && (
                        <CommandBar
                            style={{ backgroundColor: 'red' }}
                            className='commands'
                            farItems={_items}
                            items={[]}
                        />
                    )}
                    <div className='mainData'>
                        <div className='bodyLine1'>
                            <TextField
                                label={t('Address')}
                                value={formattedAddress(currentIntermediary?.address)}
                                title={formattedAddress(currentIntermediary?.address)}
                                disabled
                            />
                            {isEditable &&
                                (
                                    <IntermediaryAddress
                                        address={currentIntermediary?.address}
                                        setAddress={(address: AddressDto) => setCurrentIntermediary(
                                            new IntermediaryDetailsResponseDto({
                                                ...currentIntermediary,
                                                address
                                            })
                                        )}
                                        form={form}
                                    />
                                )}
                        </div>
                        <div className='bodyLine2s padding'>
                            <div className='bodyLine2s'>
                                <TextField
                                    label={t('EmailAddress')}
                                    value={currentIntermediary?.email}
                                    title={currentIntermediary?.email}
                                    onChange={(_, v) =>
                                        setCurrentIntermediary(
                                            new IntermediaryDetailsResponseDto({
                                                ...currentIntermediary,
                                                email: v ?? '',
                                            })
                                        )
                                    }
                                    validationFunc={(v) =>
                                        requiredItem(
                                            t,
                                            v,
                                            t('EmailAddress')
                                        )
                                    }
                                    errorMessage={apiErrors?.serviceError?.getFieldError(
                                        'Email'
                                    )}
                                    onValidationChange={(isValid) =>
                                        form.current.SetValidity('email', isValid)
                                    }
                                    disabled={!isEditable}
                                    required={isEditable}
                                    maxLength={100}
                                />
                                <TextField
                                    label={t('MobileNumber')}
                                    value={currentIntermediary?.mobileNo}
                                    title={currentIntermediary?.mobileNo}
                                    onChange={(_, v) =>
                                        setCurrentIntermediary(
                                            new IntermediaryDetailsResponseDto({
                                                ...currentIntermediary,
                                                mobileNo: v ?? '',
                                            })
                                        )
                                    }
                                    errorMessage={apiErrors?.serviceError?.getFieldError(
                                        'MobileNo'
                                    )}
                                    validationFunc={(v) =>
                                        requiredItem(
                                            t,
                                            v,
                                            t('MobileNumber')
                                        )
                                    }
                                    onValidationChange={(isValid) =>
                                        form.current.SetValidity('mobileNo', isValid)
                                    }
                                    disabled={!isEditable}
                                    required={isEditable}
                                    maxLength={14}
                                />
                            </div>
                            <TextField
                                label={t('URL')}
                                value={currentIntermediary?.url}
                                title={currentIntermediary?.url}
                                onChange={(_, v) =>
                                    setCurrentIntermediary(
                                        new IntermediaryDetailsResponseDto({
                                            ...currentIntermediary,
                                            url: v ?? '',
                                        })
                                    )
                                }
                                errorMessage={apiErrors?.serviceError?.getFieldError(
                                    'URL'
                                )}
                                onValidationChange={(isValid) =>
                                    form.current.SetValidity('url', isValid)
                                }
                                disabled={!isEditable}
                                maxLength={2000}
                            />
                        </div>
                    </div>
                    <div />
                    <div className='bodyLine2s'>
                        <div>
                            <div className="section">
                                <Section
                                    size={SectionSize.h3}
                                    title={t('ApplicationDetails')}
                                />
                                <ActionButton className='history' iconProps={{ iconName: 'History' }}
                                    onClick={() => setShowApplicationHistory(true)}>
                                    {t('History')}
                                </ActionButton>
                            </div>
                            <div className='bodyLine1'>
                                <TextField
                                    label={t('ApplicationDescription')}
                                    value={currentIntermediary?.recentApplication?.description}
                                    title={currentIntermediary?.recentApplication?.description}
                                    disabled
                                />
                            </div>
                        </div>
                        <div>
                            <div className="section">
                                <Section
                                    size={SectionSize.h3}
                                    title={t('AssignedAccess')}
                                />
                                <ActionButton className='history' iconProps={{ iconName: 'History' }}
                                    onClick={() => setShowAccessHistory(true)}>
                                    {t('History')}
                                </ActionButton>
                            </div>
                            <div className='bodyLine2s'>
                                <TextField
                                    label={t('Status')}
                                    value={isBlocked ? t('Blocked') : t('Allowed')}
                                    disabled
                                />
                            </div>
                        </div>
                    </div>
                    <div className='bodyLine2s padding'>
                        <div className='bodyLine2s'>
                            <TextField
                                label={t('Activefrom')}
                                value={UIHelpers.getLocalizedDateTime('Date', currentIntermediary?.recentActivityPeriod?.activeFrom, currentLanguage.langIso4)}
                                disabled
                            />
                            <TextField
                                label={t('Activeto')}
                                value={UIHelpers.getLocalizedDateTime('Date', currentIntermediary?.recentActivityPeriod?.activeTo, currentLanguage.langIso4)}
                                disabled
                            />
                        </div>
                    </div>
                </Panel>
                <div className='linkedTaxpayer'>
                    <Section
                        size={SectionSize.h3}
                        title={t('LinkedTaxpayer')}
                    />
                    <LinkedTaxpayerGrid
                        items={linkedTaxpayers}
                        currentLanguage={currentLanguage}
                        loadingText={isApiCallLoading ? t('common:loading') : undefined}
                        onChanged={(pageIndex: number, filteredColumn: FilteredColumn[]) => onGridChanged(pageIndex, filteredColumn)}
                        onNbItemPerPageChanged={(nbItemPerPage: number) => { onNbItemPerPageChanged(nbItemPerPage); return (true); }}
                        onPageChanged={(pageNum: number) => onPageChanged(pageNum)}
                        currentPage={currentPage}
                        pageSize={pageSize}
                        totalPages={metadata?.totalPages}
                        totalRecords={metadata?.totalCount} />
                </div>
            </div>

            {showApplicationHistory && (
                <ApplicationHistory
                    taxpayerId={currentIntermediary?.taxpayer?.taxpayerId}
                    hidden={!showApplicationHistory}
                    onClose={() => setShowApplicationHistory(false)}
                    items={currentIntermediary?.applicationHistory}
                    currentLanguage={propsCurrentLanguage} />
            )}
            {showAccessHistory && (
                <StatusHistory
                    hidden={!showAccessHistory}
                    onClose={() => setShowAccessHistory(false)}
                    items={currentIntermediary?.statusHistory}
                    currentLanguage={propsCurrentLanguage} />
            )}
            {showValidationEmail && (
                <Email
                    hidden={!showValidationEmail}
                    onClose={() => setShowValidationEmail(false)}
                    NewEmail={currentIntermediary?.email}
                    onSave={() => onSave()}
                    Type="User"
                    verifyIntermediary
                    taxpayerId={currentIntermediary?.taxpayer?.taxpayerId}
                />
            )}
            {showValidationMobile && (
                <Mobile
                    hidden={!showValidationMobile}
                    onClose={() => setShowValidationMobile(false)}
                    NewMobile={currentIntermediary?.mobileNo}
                    onSave={() => onSave()}
                    Type="User"
                    verifyIntermediary
                    taxpayerId={currentIntermediary?.taxpayer?.taxpayerId}
                />
            )}
            {showValidationEmailMobile && (
                <EmailMobile
                    hidden={!showValidationEmailMobile}
                    onClose={() => setShowValidationEmailMobile(false)}
                    NewMobile={currentIntermediary?.mobileNo}
                    NewEmail={currentIntermediary?.email}
                    onSave={() => onSave()}
                    Type="User"
                    verifyIntermediary
                    taxpayerId={currentIntermediary?.taxpayer?.taxpayerId}
                />
            )}
        </div>
    );
};

export default connector(IntermediaryDetails);
